/*
 * Recap & Stats — final result, MVP, top performers, Web Share API.
 * Web port of RecapScreen.
 */

import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Trophy, Star, Share2 } from 'lucide-react';

import { api } from '@/lib/api';
import { formatTeamCupPoints } from '@/lib/teamCupPoints';
import { AppShell } from '@/components/AppShell';
import { formatLabel, RoundStatusPill } from './_shared';
import type { EventRecap, TournamentPlayerStat } from '@golf-app/shared';

function record(p: TournamentPlayerStat) { return `${p.wins}-${p.losses}-${p.ties}`; }

export default function Recap() {
  const { eventId } = useParams<{ eventId: string }>();
  const [recap, setRecap] = useState<EventRecap | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!eventId) return;
    try { setRecap(await api.getEventRecap(eventId)); }
    catch (e: any) { setError(e?.message ?? "Couldn't load recap"); }
    finally { setLoading(false); }
  }, [eventId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <AppShell back={{ to: `/app/tournaments/${eventId}`, label: 'Event' }}>
        <div className="t-loading"><div className="spinner" /></div>
      </AppShell>
    );
  }
  if (error || !recap) {
    return (
      <AppShell back={{ to: `/app/tournaments/${eventId}`, label: 'Event' }}>
        <div className="t-empty"><div className="t-empty-title">{error ?? 'No recap yet'}</div></div>
      </AppShell>
    );
  }

  const teamA = recap.teams[0];
  const teamB = recap.teams[1];
  const teamMap = new Map(recap.teams.map((t) => [t.id, t]));
  const headline = recap.isTied ? 'All Square' : recap.winnerTeam ? `${recap.winnerTeam.name} ${recap.event.status === 'completed' ? 'win the cup' : 'lead'}` : '—';

  async function onShare() {
    const r = recap!;
    const lines = [
      `🏆 ${r.event.name}`,
      teamA && teamB ? `${teamA.name} ${formatTeamCupPoints(teamA.totalPoints)} — ${formatTeamCupPoints(teamB.totalPoints)} ${teamB.name}` : '',
      r.isTied ? 'All square!' : r.winnerTeam ? `${r.winnerTeam.name} ${r.event.status === 'completed' ? 'win!' : 'lead'}` : '',
      r.mvp ? `MVP: ${r.mvp.displayName} (${record(r.mvp)}, ${formatTeamCupPoints(r.mvp.pointsEarned)} pts)` : '',
      'via 18Gimmies',
    ].filter(Boolean);
    const text = lines.join('\n');
    if (navigator.share) {
      try { await navigator.share({ title: r.event.name, text }); }
      catch { /* user cancelled */ }
    } else {
      try {
        await navigator.clipboard.writeText(text);
        alert('Recap copied to clipboard!');
      } catch { alert(text); }
    }
  }

  return (
    <AppShell back={{ to: `/app/tournaments/${eventId}`, label: 'Event' }}>
      <section className="t-hero"><h1>Recap</h1></section>

      {/* Result card */}
      <div className="t-scoreboard" style={{ flexDirection: 'column', borderColor: 'var(--gold)', textAlign: 'center', gap: 10 }}>
        {!recap.isTied && recap.winnerTeam && (
          <Trophy width={28} height={28} strokeWidth={1.6} style={{ color: 'var(--gold)' }} />
        )}
        <div style={{ fontSize: 22, fontWeight: 900, color: 'var(--text)', letterSpacing: 0.2 }}>{headline}</div>
        {teamA && teamB && (
          <div style={{ display: 'flex', alignItems: 'center', alignSelf: 'stretch', marginTop: 14 }}>
            <div className="t-sb-team">
              <p className="t-sb-name" style={{ color: teamA.primaryColor ?? 'var(--text)' }}>{teamA.name}</p>
              <p className="t-sb-points" style={{ color: recap.winnerTeam?.id === teamA.id ? 'var(--text)' : 'var(--text-sec)' }}>
                {formatTeamCupPoints(teamA.totalPoints)}
              </p>
            </div>
            <span className="t-sb-dash">—</span>
            <div className="t-sb-team">
              <p className="t-sb-name" style={{ color: teamB.primaryColor ?? 'var(--text)' }}>{teamB.name}</p>
              <p className="t-sb-points" style={{ color: recap.winnerTeam?.id === teamB.id ? 'var(--text)' : 'var(--text-sec)' }}>
                {formatTeamCupPoints(teamB.totalPoints)}
              </p>
            </div>
          </div>
        )}
        {recap.event.locationName && <p className="t-hint">{recap.event.locationName}</p>}
        <button className="t-btn t-btn-primary" style={{ marginTop: 14 }} onClick={onShare}>
          <Share2 /> Share Result
        </button>
      </div>

      {/* MVP */}
      {recap.mvp && (
        <div className="t-row" style={{ cursor: 'default', marginTop: 16 }}>
          <Star width={20} height={20} fill="var(--gold)" stroke="var(--gold)" />
          <div className="t-row-body">
            <div className="t-section-label" style={{ margin: 0 }}>MVP</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text)', marginTop: 2 }}>{recap.mvp.displayName}</div>
          </div>
          <div style={{ fontSize: 13, color: 'var(--green-bright)', fontWeight: 700 }}>
            {record(recap.mvp)} · {formatTeamCupPoints(recap.mvp.pointsEarned)} pts
          </div>
        </div>
      )}

      {/* Top players */}
      {recap.topPlayers.length > 0 && (
        <>
          <div className="t-section-label">Top performers</div>
          <div>
            {recap.topPlayers.map((p, i) => (
              <div key={p.eventPlayerId} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 0', borderBottom: '1px solid var(--border)' }}>
                <span style={{ width: 18, fontSize: 13, fontWeight: 800, color: 'var(--text-mut)' }}>{i + 1}</span>
                <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 5, background: teamMap.get(p.teamId ?? '')?.primaryColor ?? 'var(--text-mut)' }} />
                <span style={{ flex: 1, fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>{p.displayName}</span>
                <span style={{ fontSize: 13, color: 'var(--text-sec)', fontWeight: 600, width: 60, textAlign: 'right' }}>{record(p)}</span>
                <span style={{ fontSize: 13, color: 'var(--green-bright)', fontWeight: 800, width: 60, textAlign: 'right' }}>{formatTeamCupPoints(p.pointsEarned)} pts</span>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Rounds */}
      {recap.rounds.length > 0 && (
        <>
          <div className="t-section-label">Rounds</div>
          <div className="t-list">
            {recap.rounds.map((r) => (
              <div key={r.id} className="t-row" style={{ cursor: 'default' }}>
                <div className="t-row-body">
                  <div className="t-row-name">{r.name}</div>
                  <div className="t-row-sub">{formatLabel(r.formatType)}</div>
                </div>
                <RoundStatusPill status={r.status} />
              </div>
            ))}
          </div>
        </>
      )}
    </AppShell>
  );
}
